import { useEffect, useState, useRef } from "react";
import { useLocation } from "wouter";
import Clarity from "@microsoft/clarity";

const CLARITY_PROJECT_ID = "pbupj2lfmc";

// Extend window type for Clarity global API
declare global {
  interface Window {
    clarity?: (method: string, ...args: string[]) => void;
  }
}

/**
 * Hook for initializing Microsoft Clarity analytics
 *
 * GDPR Compliant: Only initializes after user accepts cookies.
 * Checks localStorage for 'cookieConsent' === 'accepted' (set by CookieConsent component)
 *
 * Features tracked by Clarity:
 * - Heatmaps (click patterns)
 * - Session recordings
 * - Scroll depth
 * - Dead clicks (clicks on non-interactive elements)
 * - Rage clicks (frustrated repeated clicks)
 * - User journey analysis
 * - SPA page navigation (automatically tracked)
 */
export function useClarity() {
  const [isInitialized, setIsInitialized] = useState(false);
  const [location] = useLocation();
  const previousLocation = useRef(location);

  // Initialize Clarity
  useEffect(() => {
    const hasConsent = localStorage.getItem("cookieConsent") === "accepted";

    if (hasConsent && !isInitialized) {
      try {
        Clarity.init(CLARITY_PROJECT_ID);

        if (window.clarity) {
          window.clarity("consent");
        }

        setIsInitialized(true);

        if (process.env.NODE_ENV === "development") {
          console.log("[Clarity] Initialized with project:", CLARITY_PROJECT_ID);
        }
      } catch (error) {
        console.error("[Clarity] Failed to initialize:", error);
        if (process.env.NODE_ENV === "development") {
          console.log("[Clarity] Debug - window.clarity exists:", !!window.clarity);
        }
      }
    }
  }, [isInitialized]);

  // Track SPA page navigation
  useEffect(() => {
    if (!isInitialized) return;

    // Only track if location actually changed
    if (previousLocation.current !== location) {
      try {
        if (window.clarity) {
          window.clarity("set", "page", location);
          window.clarity("event", `pageview:${location}`);
        }

        if (process.env.NODE_ENV === "development") {
          console.log("[Clarity] Page navigation tracked:", location);
        }
      } catch (error) {
        console.error("[Clarity] Failed to track page navigation:", error);
        if (process.env.NODE_ENV === "development") {
          console.log("[Clarity] Debug - location:", location);
        }
      }

      previousLocation.current = location;
    }
  }, [location, isInitialized]);

  // Listen for cookie consent changes (in case user accepts after page load)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "cookieConsent" && e.newValue === "accepted" && !isInitialized) {
        initClarity();
      }
    };

    const handleConsentAccepted = () => {
      if (!isInitialized) {
        initClarity();
      }
    };

    const initClarity = () => {
      try {
        Clarity.init(CLARITY_PROJECT_ID);

        if (window.clarity) {
          window.clarity("consent");
        }

        setIsInitialized(true);

        if (process.env.NODE_ENV === "development") {
          console.log("[Clarity] Initialized after consent");
        }
      } catch (error) {
        console.error("[Clarity] Failed to initialize on consent:", error);
        if (process.env.NODE_ENV === "development") {
          console.log("[Clarity] Debug - window.clarity exists:", !!window.clarity);
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("cookieConsentAccepted", handleConsentAccepted);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("cookieConsentAccepted", handleConsentAccepted);
    };
  }, [isInitialized]);

  return { isInitialized };
}

/**
 * Identify a user in Clarity for better session analysis
 * Call this after user logs in
 *
 * @param userId - Unique user identifier (avoid PII like email)
 * @param sessionId - Optional session identifier
 * @param pageId - Optional page identifier
 * @param friendlyName - Optional human-readable name (e.g., "Premium User")
 */
export function identifyClarityUser(
  userId: string,
  sessionId?: string,
  pageId?: string,
  friendlyName?: string
) {
  try {
    if (window.clarity) {
      window.clarity("identify", userId, sessionId || "", pageId || "", friendlyName || "");
    }
  } catch (error) {
    console.error("[Clarity] Failed to identify user:", error);
  }
}

/**
 * Set a custom tag in Clarity for filtering sessions
 * Useful for A/B tests, user segments, feature flags
 *
 * @param key - Tag name (e.g., "user_type", "experiment_variant")
 * @param value - Tag value or array of values (e.g., "premium", ["feature_a", "feature_b"])
 */
export function setClarityTag(key: string, value: string | string[]) {
  try {
    if (window.clarity) {
      if (Array.isArray(value)) {
        window.clarity("set", key, ...value);
      } else {
        window.clarity("set", key, value);
      }
    }
  } catch (error) {
    console.error("[Clarity] Failed to set tag:", error);
  }
}

/**
 * Trigger a custom event in Clarity
 * Use for tracking specific user actions
 *
 * @param eventName - Name of the event (e.g., "purchase_completed", "form_submitted")
 */
export function trackClarityEvent(eventName: string) {
  try {
    if (window.clarity) {
      window.clarity("event", eventName);
    }
  } catch (error) {
    console.error("[Clarity] Failed to track event:", error);
  }
}

/**
 * Upgrade the current session priority in Clarity
 * Call this when user performs high-value actions (purchase, signup)
 * This ensures the session is recorded even if sampling would skip it
 *
 * @param reason - Why this session is important (e.g., "completed_purchase")
 */
export function upgradeClaritySession(reason: string) {
  try {
    if (window.clarity) {
      window.clarity("upgrade", reason);
    }
  } catch (error) {
    console.error("[Clarity] Failed to upgrade session:", error);
  }
}

/**
 * Utility to mark important sessions for prioritized recording
 * Call this on key conversion events
 */
export function markConversionSession(conversionType:
  | "purchase"
  | "signup"
  | "consultation_booked"
  | "recipe_purchased"
  | "contact_form"
) {
  setClarityTag("conversion", conversionType);
  upgradeClaritySession(conversionType);
  trackClarityEvent(`conversion:${conversionType}`);
}
